import React, { ReactNode, createRef } from 'react';

import { useWindowEvent } from '~shared/hooks';

import { CLOSE_MENU } from './actions';
import { useMenuReducer } from './hooks/useMenuReducer';
import { MenuContext } from './MenuContext';

export interface Props {
  children: ReactNode;
  initialOpen?: boolean;
}

/**
 * Build custom, accessible dropdown components with robust support for
 * keyboard navigation.
 *
 * Menus are best for UI elements that resemble things like the menus found in
 * the title bar of most operating systems. They have specific accessibility
 * semantics, and their content should be restricted to a list of links or
 * buttons. Focus is trapped in an open menu, so you cannot Tab through the
 * content or away from the menu. Instead, the arrow keys navigate through a
 * Menu's items.
 *
 * Sets up the initial menu state and renders a context provider for the
 * child sub-components.
 */
export function Menu({ children, initialOpen = false }: Props) {
  const reducerBag = useMenuReducer({
    isOpen: initialOpen,
    buttonRef: createRef(),
    listRef: createRef(),
    items: [],
    searchQuery: '',
    activeItemIndex: null,
  });
  const [{ isOpen, buttonRef, listRef }, dispatch] = reducerBag;

  useWindowEvent('mousedown', (event) => {
    const target = event.target as HTMLElement;

    if (!isOpen) return;
    if (buttonRef.current?.contains(target)) return;
    if (listRef.current?.contains(target)) return;

    dispatch({ type: CLOSE_MENU });
    event.preventDefault();
    buttonRef.current?.focus();
  });

  return (
    <MenuContext.Provider value={reducerBag}>
      <div className="relative inline-block w-full text-left">{children}</div>
    </MenuContext.Provider>
  );
}
